import { useCallback, useMemo, type ReactNode } from "react";

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";

const modifiers = [restrictToVerticalAxis, restrictToParentElement];

type SortableListProps<T extends string> = {
  items: T[];
  onReorder: (next: T[]) => void;
  renderItem: (id: T, index: number) => ReactNode;
  announcements?: Announcements;
};

/**
 * Generic vertical sortable list. Each `<SortableList>` owns its own `DndContext`.
 */
const SortableList = <T extends string>({
  items,
  onReorder,
  renderItem,
  announcements,
}: SortableListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const fromIndex = items.findIndex((id) => id === active.id);
      const toIndex = items.findIndex((id) => id === over.id);
      if (fromIndex < 0 || toIndex < 0) {
        return;
      }
      onReorder(arrayMove(items, fromIndex, toIndex));
    },
    [items, onReorder]
  );

  const accessibility = useMemo(
    () => (announcements ? { announcements } : undefined),
    [announcements]
  );

  return (
    <DndContext
      autoScroll={false}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragEnd={handleDragEnd}
      accessibility={accessibility}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((id, index) => renderItem(id, index))}
      </SortableContext>
    </DndContext>
  );
};

export default SortableList;
